import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [loadingInvite, setLoadingInvite] = useState(true);
  const [inviteError, setInviteError] = useState('');

  useState(() => {
    if (token) {
      axios.get(`${API}/api/invites/validate?token=${token}`)
        .then(res => { setInviteInfo(res.data); setLoadingInvite(false); })
        .catch(err => { setInviteError(err.response?.data?.detail || 'Invalid invite'); setLoadingInvite(false); });
    } else {
      setLoadingInvite(false);
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Please enter your name'); return; }
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (!token) { toast.error('Invalid invite link'); return; }

    setSubmitting(true);
    try {
      await axios.post(`${API}/api/invites/accept`, { token, name: name.trim(), password });
      setSuccess(true);
      toast.success('Account created! Redirecting to login...');
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  };

  if (!token || inviteError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF9] p-4">
        <Card className="w-full max-w-md rounded-2xl border border-stone-200 shadow-sm">
          <CardContent className="p-8 text-center">
            <h2 className="text-xl font-bold text-stone-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Invalid Invite Link</h2>
            <p className="text-stone-500 text-sm mb-4">{inviteError || 'This invite link is invalid or has expired. Please ask your manager for a new invite.'}</p>
            <Button onClick={() => navigate('/login')} className="bg-[#1B2A4A] text-white rounded-xl hover:bg-[#243658]">Go to Login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loadingInvite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF9]">
        <Loader2 className="w-8 h-8 animate-spin text-[#1B2A4A]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAF9] p-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-[#1B2A4A]" style={{ fontFamily: "'Outfit', sans-serif" }}>GRILLSMITH</h2>
          <p className="text-[10px] tracking-[3px] text-stone-400 font-medium">CRAFT BY FIRE - LMS</p>
        </div>

        <Card className="rounded-2xl border border-stone-200 shadow-sm">
          <CardContent className="p-6 sm:p-8">
            {success ? (
              <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-4">
                <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-stone-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Welcome to the Team!</h3>
                <p className="text-stone-500 text-sm">Your account has been created. Redirecting to login...</p>
              </motion.div>
            ) : (
              <>
                <h3 className="text-lg font-bold text-stone-900 mb-1 text-center" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Join GrillSmith LMS
                </h3>
                <p className="text-stone-500 text-sm text-center mb-2">Set up your account to get started with training</p>
                {inviteInfo && (
                  <div className="bg-stone-50 rounded-xl p-3 mb-4 text-center text-sm">
                    <p className="text-stone-700">Invite for <span className="font-semibold">{inviteInfo.email}</span></p>
                    <div className="flex items-center justify-center gap-2 mt-1 flex-wrap">
                      {inviteInfo.role && <span className="text-xs bg-[#1B2A4A]/10 text-[#1B2A4A] px-2 py-0.5 rounded-full">{inviteInfo.role}</span>}
                      {inviteInfo.department && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{inviteInfo.department}</span>}
                      {inviteInfo.position && <span className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full">{inviteInfo.position}</span>}
                      {inviteInfo.location && <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">{inviteInfo.location}</span>}
                    </div>
                  </div>
                )}
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label className="text-stone-700 text-sm">Full Name</Label>
                    <Input value={name} onChange={e => setName(e.target.value)} placeholder="Your full name"
                      className="rounded-xl mt-1" autoFocus data-testid="invite-name" />
                  </div>
                  <div>
                    <Label className="text-stone-700 text-sm">Password</Label>
                    <Input type="password" value={password} onChange={e => setPassword(e.target.value)}
                      placeholder="Choose a password (min 6 chars)" className="rounded-xl mt-1" data-testid="invite-password" />
                  </div>
                  <div>
                    <Label className="text-stone-700 text-sm">Confirm Password</Label>
                    <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                      placeholder="Confirm password" className="rounded-xl mt-1" data-testid="invite-confirm-password" />
                  </div>
                  <Button type="submit" disabled={submitting} className="w-full bg-[#1B2A4A] text-white rounded-xl hover:bg-[#243658]" data-testid="invite-submit-btn">
                    {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating Account...</> : 'Create My Account'}
                  </Button>
                </form>
                <p className="text-xs text-stone-400 text-center mt-4">
                  Already have an account? <a href="/login" className="text-[#1B2A4A] font-medium hover:underline">Sign in</a>
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
